import { Fragment, useEffect, useContext } from 'react';
import { history, useLocation, useParams } from 'umi';
import { useDispatch } from 'dva';
import { useIntl } from 'react-intl';
import { Row, Col } from 'antd';
import { Form, message } from 'iglooform';
import throttle from 'lodash/throttle';
import { setPaymentPassword } from '../service';
import { BackButton } from '@/components/back-button/back-button';
import { HeaderTitle } from '@/components/header-title/header-title';
import { aesDecrypt, EnterKeyPressListener } from '@/utils/common';
import Context from '@/utils/context';
import { payNumberReg } from '@/utils/regexp';

const SetPaymentPassword = () => {
  const form = Form.useForm();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { isPc } = useContext(Context);
  const { pathname }: any = useLocation();
  const { pay_pass }: any = useParams();

  useEffect(() => {
    EnterKeyPressListener(handlePaymentPassword);
    return () => {
      document.body.removeEventListener('keydown', handlePaymentPassword);
    };
  }, []);

  const handlePaymentPassword = throttle(
    async (): Promise<void> => {
      // 是否是修改Payment Password，如果是，则需要获取上一个页面的原payment password参数
      const isChangePaymentPassword = pathname.includes(
        '/me/change-payment-password/reset',
      );
      let old_payment_password = '';
      if (isChangePaymentPassword) {
        old_payment_password = aesDecrypt(pay_pass); // 解密payment password
      }
      try {
        const values = await form.validateFields();
        setPaymentPassword(
          isChangePaymentPassword
            ? {
                old_payment_password,
                payment_password: values.pinNumber.trim(),
              }
            : {
                payment_password: values.pinNumber.trim(),
              },
        ).then((res) => {
          if (res) {
            message.success(
              formatMessage({
                id: 'Set payment password successfully!',
              }),
            );
            history.push('/home');
            dispatch({
              type: 'global/setData',
              payload: {
                menuKey: 'home',
              },
            });
          }
        });
      } catch (error) {
        console.error('set payment password error: ', error);
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  return (
    <Fragment>
      <BackButton handleClick={() => history.push('/login')} />
      <Row style={isPc ? { marginTop: '40px' } : { marginTop: '24px' }}>
        <Col
          span={isPc ? 12 : 24}
          style={{ maxWidth: '538px', margin: '0 auto' }}
        >
          <HeaderTitle
            title={formatMessage({ id: 'Set Payment Password' })}
            subTitle={formatMessage({
              id: 'Create a payment password for critical transactions.',
            })}
          />
          <div className="form-container">
            <Form
              form={form}
              config={{
                type: 'Login',
                elements: [
                  {
                    type: 'Password',
                    label: formatMessage({
                      id: 'Payment Password',
                      defaultMessage: 'Payment Password',
                    }),
                    name: 'pinNumber',
                    fullRow: true,
                    required: true,
                    initialValue: '',
                    help: formatMessage({
                      id: '6 digital number',
                      defaultMessage: '6 digital number',
                    }),
                    rules: [
                      {
                        validator: (rule: any, value: string) => {
                          const p = form.getFieldValue('confirmPinNumber');
                          if (!!value && !payNumberReg.test(value?.trim())) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Payment password must 6 length!',
                                }),
                              ),
                            );
                          } else if (!!p && p !== value) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The two payment password is inconsistent!',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                    antdDependencies: ['confirmPinNumber'],
                  },
                  {
                    type: 'Password',
                    label: formatMessage({
                      id: 'Confirm Payment Password',
                      defaultMessage: 'Confirm Payment Password',
                    }),
                    name: 'confirmPinNumber',
                    fullRow: true,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (rule: any, value: string) => {
                          const p = form.getFieldValue('pinNumber');
                          if (!!value && p !== value) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The two payment password is inconsistent!',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                    antdDependencies: ['pinNumber'],
                  },
                ],
              }}
              onSubmit={handlePaymentPassword}
              locales={{
                submitText: formatMessage({
                  id: 'Done',
                  defaultMessage: 'Done',
                }),
                validateMessages: {
                  required:
                    '${label} ' +
                    formatMessage({
                      id: 'is required',
                    }),
                },
              }}
            />
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

export default SetPaymentPassword;
